import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { CognitoService } from 'src/app/auth/services/cognito.service';
import {
  CodigoRedCripto,
  MEDIO_CRIPTO_TRC20,
  REDES_CRIPTO,
} from 'src/app/shared/utils/red-cripto';
import { environment } from 'src/environments/environment';

/** Una red de `medio_criptomonedas`, como la devuelve `GET medios-cripto` de Nest. */
export interface MedioCripto {
  id: number;
  descripcion: string;
  /** `null` si el id no está en el catálogo de Nest. */
  codigo: CodigoRedCripto | null;
  activo: boolean;
  pagaBinance: boolean;
  /** Correo del operador que la cambió por última vez; `null` si nunca se tocó. */
  actualizadoPor: string | null;
  /** Cuándo (ISO 8601); `null` si nunca se tocó. */
  actualizadoEn: string | null;
}

/**
 * Estado de las redes cripto (encendida/pausada) en Nest.
 *
 * El cliente lo usa para avisar y deshabilitar antes de que el backend
 * rebote; el operador, para prender y apagar cada red. El candado de verdad
 * son los backends (422 `RED_CRIPTO_INACTIVA`), no esto.
 */
@Injectable({ providedIn: 'root' })
export class MediosCriptoService {
  private baseUrl = environment.apiNest;

  /**
   * Cuánto vale una lectura. Corto a propósito: alcanza para que el listado y
   * el diálogo de alta que se abre encima no pregunten dos veces, pero un
   * cambio del operador llega enseguida a la pantalla siguiente.
   */
  static readonly CACHE_MS = 30_000;

  /** La promesa y no el resultado: dos pedidos simultáneos comparten la misma llamada. */
  private cache: { hasta: number; lectura: Promise<MedioCripto[]> } | null = null;

  constructor(
    private http: HttpClient,
    private cognitoService: CognitoService,
  ) {}

  /**
   * Estado de las redes para las pantallas del cliente. Nunca rechaza.
   *
   * Si la consulta falla —Nest sin desplegar, sin sesión, lo que sea—
   * devuelve las redes conocidas como activas y no lo deja en caché, así la
   * próxima pantalla vuelve a preguntar. Es deliberado: el backend vuelve a
   * mirar la red al registrar y al crear, y apagarle al cliente algo que
   * funciona porque una lectura falló sería romper el flujo de siempre.
   */
  listar(): Promise<MedioCripto[]> {
    if (this.cache && this.cache.hasta > Date.now()) {
      return this.cache.lectura;
    }
    // El respaldo va dentro de la promesa que se comparte: quien llegue
    // mientras la consulta está en vuelo tampoco puede recibir un rechazo.
    const lectura: Promise<MedioCripto[]> = this.consultar().catch((error) => {
      if (this.cache?.lectura === lectura) {
        this.cache = null;
      }
      console.warn('[medios-cripto] no se pudo leer el estado de las redes', error);
      return MediosCriptoService.redesConocidasActivas();
    });
    this.cache = { hasta: Date.now() + MediosCriptoService.CACHE_MS, lectura };
    return lectura;
  }

  /**
   * Lectura directa, sin caché ni red de seguridad: lanza si falla. La usa el
   * panel del operador, que tiene que saber si está viendo el estado real
   * antes de ofrecer los interruptores.
   */
  async consultar(): Promise<MedioCripto[]> {
    const headers = await this.getHeaders();
    const respuesta = await firstValueFrom(
      this.http.get<{ success: boolean; data: MedioCripto[] }>(
        `${this.baseUrl}medios-cripto`,
        { headers },
      ),
    );
    if (!Array.isArray(respuesta?.data)) {
      throw new Error('Respuesta inesperada de medios-cripto');
    }
    return respuesta.data
      .map((fila) => MediosCriptoService.normalizar(fila))
      .sort((a, b) => a.id - b.id);
  }

  /** Olvida la última lectura: la próxima `listar()` vuelve a preguntar. */
  invalidar(): void {
    this.cache = null;
  }

  /**
   * Enciende o pausa una red. Solo operadores (Nest lo valida).
   *
   * POST y no PATCH: el CORS de Nest no deja pasar PATCH, y Nest expone el
   * mismo cambio por POST (como en otros módulos). `activo` va como boolean
   * estricto: un "false" en texto Nest lo rechaza con 400.
   */
  async cambiarEstado(id: number, activo: boolean): Promise<MedioCripto> {
    const headers = await this.getHeaders();
    try {
      const respuesta = await firstValueFrom(
        this.http.post<{ success: boolean; data: MedioCripto }>(
          `${this.baseUrl}medios-cripto/${id}`,
          { activo: activo === true },
          { headers },
        ),
      );
      return MediosCriptoService.normalizar(respuesta?.data ?? { id, activo });
    } finally {
      // Haya salido o no, lo que había en caché ya no se puede creer.
      this.invalidar();
    }
  }

  /** Las redes del catálogo, todas activas: lo que se asume cuando no hay lectura. */
  static redesConocidasActivas(): MedioCripto[] {
    return REDES_CRIPTO.map((red) => ({
      id: red.id,
      descripcion: red.descripcionBd,
      codigo: red.codigo,
      activo: true,
      pagaBinance: red.pagaBinance,
      actualizadoPor: null,
      actualizadoEn: null,
    }));
  }

  /**
   * Una fila tal cual llegó, con tipos firmes. `activo` puede venir como 0/1
   * si algún día Nest devuelve la columna cruda; solo un "no" explícito apaga.
   * `actualizadoPor`/`actualizadoEn` llegaron después al DTO: si faltan, null.
   */
  private static normalizar(fila: any): MedioCripto {
    const id = Number(fila?.id);
    const red = REDES_CRIPTO.find((r) => r.id === id);
    const activo = fila?.activo;
    const texto = (v: unknown): string | null =>
      typeof v === 'string' && v.trim() ? v.trim() : null;
    return {
      id,
      descripcion: String(fila?.descripcion ?? red?.descripcionBd ?? `Red ${id}`),
      codigo: (fila?.codigo ?? red?.codigo ?? null) as CodigoRedCripto | null,
      activo: !(activo === false || activo === 0 || activo === '0'),
      pagaBinance:
        typeof fila?.pagaBinance === 'boolean' ? fila.pagaBinance : id === MEDIO_CRIPTO_TRC20,
      actualizadoPor: texto(fila?.actualizadoPor),
      actualizadoEn: texto(fila?.actualizadoEn),
    };
  }

  private async getHeaders(): Promise<HttpHeaders> {
    const session = await this.cognitoService.getSession();
    if (!session?.accessToken?.jwtToken) {
      throw new Error('No se pudo obtener el token de sesión');
    }
    return new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('Authorization', `Bearer ${session.accessToken.jwtToken.trim()}`);
  }
}
